import { Row, Col, Statistic, Card, Divider } from "antd";
import {
  DollarOutlined,
  ShoppingOutlined,
  UserOutlined,
  GiftOutlined,
} from "@ant-design/icons";
import { CashbackReportSummary as SummaryType } from "../hooks/useCashbackReport";

interface CashbackReportSummaryProps {
  summary?: SummaryType;
}

export const CashbackReportSummary = ({
  summary,
}: CashbackReportSummaryProps) => {
  if (!summary) {
    return <div>No hay datos de resumen disponibles</div>;
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat("es-CO").format(value);
  };

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <div style={{ textAlign: "center", padding: "16px" }}>
            <div
              style={{ fontSize: "14px", color: "#666", marginBottom: "8px" }}
            >
              Total Cashback
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <DollarOutlined style={{ fontSize: "24px", color: "#3f8600" }} />
              <span
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#3f8600",
                }}
              >
                {formatCurrency(summary.total_cashback_amount)}
              </span>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div style={{ textAlign: "center", padding: "16px" }}>
            <div
              style={{ fontSize: "14px", color: "#666", marginBottom: "8px" }}
            >
              Total Órdenes
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <ShoppingOutlined
                style={{ fontSize: "24px", color: "#1890ff" }}
              />
              <span
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#1890ff",
                }}
              >
                {formatNumber(summary.total_orders)}
              </span>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div style={{ textAlign: "center", padding: "16px" }}>
            <div
              style={{ fontSize: "14px", color: "#666", marginBottom: "8px" }}
            >
              Total Productos
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <GiftOutlined style={{ fontSize: "24px", color: "#722ed1" }} />
              <span
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#722ed1",
                }}
              >
                {formatNumber(summary.total_products)}
              </span>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div style={{ textAlign: "center", padding: "16px" }}>
            <div
              style={{ fontSize: "14px", color: "#666", marginBottom: "8px" }}
            >
              Total Usuarios
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <UserOutlined style={{ fontSize: "24px", color: "#eb2f96" }} />
              <span
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#eb2f96",
                }}
              >
                {formatNumber(summary.total_users)}
              </span>
            </div>
          </div>
        </Col>
      </Row>

      <Divider />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card title="Por Marca" size="small">
            {Object.entries(summary.by_brand).map(([brand, data]) => (
              <div key={brand} style={{ marginBottom: "8px" }}>
                <div style={{ fontWeight: "bold" }}>{brand}</div>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  Cashback: {formatCurrency(data.total_cashback)} | Órdenes:{" "}
                  {formatNumber(data.total_orders)} | Productos:{" "}
                  {formatNumber(data.total_products)}
                </div>
              </div>
            ))}
            {Object.keys(summary.by_brand).length === 0 && (
              <div style={{ color: "#999", fontStyle: "italic" }}>
                No hay datos por marca
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title="Por Categoría" size="small">
            {Object.entries(summary.by_category).map(([category, data]) => (
              <div key={category} style={{ marginBottom: "8px" }}>
                <div style={{ fontWeight: "bold" }}>{category}</div>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  Cashback: {formatCurrency(data.total_cashback)} | Órdenes:{" "}
                  {formatNumber(data.total_orders)} | Productos:{" "}
                  {formatNumber(data.total_products)}
                </div>
              </div>
            ))}
            {Object.keys(summary.by_category).length === 0 && (
              <div style={{ color: "#999", fontStyle: "italic" }}>
                No hay datos por categoría
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title="Por Estado Producto" size="small">
            {Object.entries(summary.by_product_status || {}).map(
              ([status, data]) => (
                <div key={status} style={{ marginBottom: "8px" }}>
                  <div style={{ fontWeight: "bold" }}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    Cashback: {formatCurrency(data.total_cashback)} | Órdenes:{" "}
                    {formatNumber(data.total_orders)} | Productos:{" "}
                    {formatNumber(data.total_products)}
                  </div>
                </div>
              )
            )}
            {(!summary.by_product_status ||
              Object.keys(summary.by_product_status).length === 0) && (
              <div style={{ color: "#999", fontStyle: "italic" }}>
                No hay datos por estado
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};
